import './NewTable.scss';
import { useMemo } from 'react';
import { NewTableProps } from './types';
import { NTProvider } from './NTContext';
import { NTScrollProvider } from './NTScrollContext';
import { NTUtilsProvider } from './NTUtilsContext';
import { NTSelectionProvider } from './NTSelectionContext';
import { NTTableProvider } from './NTTableContext';
import NTProfiler from './NTProfiler';
import { NewTableContent } from './NewTableContent';
import { readURLParams } from './utils/urlParams';

// Helper function to adapt TableFilters to the filters type expected by NTProvider
const adaptFilters = (tableFilters: any) => {
  return {
    ...tableFilters,
    sparkLine: false, // Add missing properties
    collapsed: false,
  };
};

const NewTable = ({
  report,
  reportId,
  fixFirstColumn = true,
  fixLastColumn = false,
  loading = false,
  redraw = false,
  reportType = 'report',
}: NewTableProps) => {
  // Read URL parameters once at the top level
  const { filter, actions: urlActions, selection, urlBudgetInclude, accountVFilters } = readURLParams();

  const MemoizedNewTableContent = useMemo(
    () => (
      <NewTableContent
        report={report}
        reportId={reportId}
        fixFirstColumn={fixFirstColumn}
        fixLastColumn={fixLastColumn}
        loading={loading}
        redraw={redraw}
        reportType={reportType}
        // Pass URL parameters to NewTableContent
        urlParams={{
          filter,
          urlActions,
          selection,
          urlBudgetInclude,
          accountVFilters,
        }}
      />
    ),
    [
      report?.createdAt,
      report?.dates,
      fixFirstColumn,
      fixLastColumn,
      loading,
      redraw,
      reportType,
      // Include URL parameters in dependency array to re-render when they change
      JSON.stringify({ filter, urlActions, selection, urlBudgetInclude, accountVFilters }),
    ]
  );

  return (
    <NTProvider initialFilters={adaptFilters(filter)} initialBudgetAvailable={urlBudgetInclude}>
      <NTProfiler>
        <NTScrollProvider>
          <NTUtilsProvider initialActions={urlActions} initialAccountVisualFilters={accountVFilters}>
            <NTSelectionProvider initialSelection={selection}>
              <NTTableProvider>{MemoizedNewTableContent}</NTTableProvider>
            </NTSelectionProvider>
          </NTUtilsProvider>
        </NTScrollProvider>
      </NTProfiler>
    </NTProvider>
  );
};

export default NewTable;
